import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ArrowUp, Share, MousePointer2, MoreVertical, PlusSquare, Smartphone } from 'lucide-react';

interface InstallGuideProps {
  onClose: () => void;
  logoUrl?: string;
}

export const InstallGuide: React.FC<InstallGuideProps> = ({ onClose, logoUrl }) => {
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(userAgent));
  }, []);

  return (
    <AnimatePresence>
      {/* Overlay Oscuro para enfoque total */}
      <motion.div
        key="pwa-install-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999]"
      />

      {/* Flecha Curva Animada - COMO HERMANO FUERA DE CUALQUIER TRANSFORM */}
      {!isIOS && (
        <motion.div
          key="pwa-install-arrow"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.5 }}
          className="fixed top-1 right-1 w-36 h-36 pointer-events-none z-[10001]"
        >
          <svg width="100%" height="100%" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-[0_0_15px_rgba(var(--glow-rgb),0.6)]">
            {/* Línea de flujo continuo (hacia la esquina superior derecha) */}
            <motion.path
              d="M 15 55 C 25 35, 50 20, 85 15"
              stroke="var(--primary-color, #eab308)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="10 6"
              animate={{ strokeDashoffset: [0, -32] }}
              transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: "linear"
              }}
            />

            {/* Punta de la flecha integrada y alineada, con un pulso suave */}
            <motion.path
              d="M 74 8 L 88 14 L 78 26"
              stroke="var(--primary-color, #eab308)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              animate={{ 
                x: [0, 2, 0], 
                y: [0, -2, 0],
                opacity: [0.8, 1, 0.8]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </svg>
        </motion.div>
      )}

      <motion.div
        key="pwa-install-content"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className={`fixed z-[10000] ${isIOS ? 'bottom-24' : 'top-20'} left-1/2 -translate-x-1/2 w-[310px] pointer-events-none`}
      >

        {/* Contenedor Principal con Estilo Premium - Más compacto */}
        <div className="bg-[#0f172a]/95 backdrop-blur-3xl border border-white/20 rounded-[2rem] p-5 shadow-[0_50px_120px_rgba(0,0,0,1)] relative overflow-visible pointer-events-auto">
          {/* Luces de fondo dinámicas con más intensidad */}
          <div className="absolute -top-16 -right-16 w-40 h-40 bg-theme-primary/50 blur-[60px] rounded-full animate-pulse" />
          <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-theme-secondary/30 blur-[60px] rounded-full animate-pulse" style={{ animationDelay: '1.2s' }} />
          
          <div className="relative z-10">
            {/* Cabecera con Logo - Reducida */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 flex items-center justify-center shrink-0">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="w-12 h-12 object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]" />
                ) : (
                  <img src="/logo.png" alt="Logo" className="w-12 h-12 object-contain grayscale invert brightness-0" />
                )}
              </div>
              <div>
                <h3 className="text-base font-black uppercase tracking-[0.1em] theme-primary-text leading-tight italic">
                  Instala la app
                </h3>
                <div className="mt-1.5 inline-flex items-center bg-white/5 border border-white/10 rounded-full px-2.5 py-0.5 backdrop-blur-md">
                  <p className="text-[10px] text-white/90 font-bold uppercase tracking-wider leading-none flex items-center">
                    <span>en solo</span>
                    <span className="theme-primary-text font-black px-1.5 text-[16px] leading-none drop-shadow-[0_0_6px_rgba(var(--glow-rgb),0.5)]">2</span>
                    <span>simples pasos</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Pasos de Instalación - Más compactos y con casilleros destacados */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/10 hover:bg-white/10 transition-all group">
                <div className="w-11 h-11 rounded-xl bg-theme-primary/15 border border-theme-primary/30 flex items-center justify-center theme-primary-text shrink-0 shadow-[0_0_12px_rgba(var(--glow-rgb),0.35)] group-hover:scale-105 transition-transform">
                  {isIOS ? <Share size={20} strokeWidth={2.5} /> : <MoreVertical size={20} strokeWidth={2.5} />}
                </div>
                <p className="text-[13px] text-white/95 font-bold leading-tight">
                  {isIOS 
                    ? 'Toca el icono de "Compartir" en Safari.' 
                    : 'Toca los tres puntos (⋮) arriba a la derecha.'}
                </p>
              </div>

              <div className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/10 hover:bg-white/10 transition-all group">
                <div className="w-11 h-11 rounded-xl bg-theme-primary/15 border border-theme-primary/30 flex items-center justify-center theme-primary-text shrink-0 shadow-[0_0_12px_rgba(var(--glow-rgb),0.35)] group-hover:scale-105 transition-transform">
                  <PlusSquare size={20} strokeWidth={2.5} />
                </div>
                <p className="text-[13px] text-white/95 font-bold leading-tight">
                  Busca la opción <span className="theme-primary-text font-black uppercase tracking-tight">"Agregar a inicio"</span> o similar.
                </p>
              </div>
            </div>

            {/* Botón de Acción */}
            <div className="flex justify-center">
              <button
                onClick={onClose}
                className="w-1/2 py-3 rounded-2xl bg-white/5 text-white/90 font-black uppercase tracking-[0.15em] border border-white/10 hover:border-theme-primary hover:bg-theme-primary/15 hover:text-theme-primary hover:shadow-[0_0_15px_rgba(var(--glow-rgb),0.3)] transition-all active:scale-95 shadow-xl text-[10px] cursor-pointer"
              >
                Entendido
              </button>
            </div>
          </div>

          {/* Botón Cerrar X Mejorado */}
          <button 
            onClick={onClose}
            className="absolute -top-4 -right-4 w-12 h-12 bg-[#0f172a] border border-white/30 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:border-white transition-all shadow-2xl z-20"
          >
            <X size={24} />
          </button>
        </div>

        {/* Flecha Animada para iOS - ABAJO */}
        {isIOS && (
          <div className="flex justify-center mt-10 text-theme-primary">
            <motion.div
              animate={{ y: [0, 20, 0] }}
              transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowUp className="rotate-180" size={64} strokeWidth={3.5} />
            </motion.div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
